<template>
	<div
		class="bento-tile bento-tile--dark api-key-tile"
		data-test="api-key-tile"
	>
		<div class="api-key-tile__intro">
			<span class="bento-tile__eyebrow api-key-tile__title">Compute Engine API Key</span>

			<div class="api-key-tile__body sgnl-caption-regular">
				<template v-if="loading">&nbsp;</template>
				<template v-else-if="hasKey"
					>You have {{ keys.length }} active key{{ keys.length === 1 ? "" : "s" }}.</template
				>
				<template v-else>Create your first Compute Engine API key.</template>
			</div>
		</div>

		<div class="api-key-tile__pictogram" />

		<v-btn
			variant="text"
			size="small"
			:to="{ name: 'Settings_cloudEngineAPI' }"
			class="bento-tile__action api-key-tile__settings"
		>
			API Settings
			<ArrowRight16 />
		</v-btn>

		<div class="api-key-tile__actions">
			<LimitableActionButton
				variant="flat"
				color="primary"
				block
				data-test="api-key-tile-create"
				:loading="creating"
				:limits-to-check="[UserLimitsE.KeyCount]"
				@valid-click="createKey"
			>
				<template #btnContent>
					<Add16 />
					<span>{{ hasKey ? "New key" : "Create API key" }}</span>
				</template>
			</LimitableActionButton>
		</div>

		<APIKeyCreationDialog
			v-if="showKeyCreationDialog"
			v-model:showDialog="showKeyCreationDialog"
			:passed-name="keyCreationDialogConfig?.name"
			:passed-key="keyCreationDialogConfig?.key"
		/>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Add16 from "@carbon/icons-vue/es/add/16";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";

import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import APIKeyCreationDialog from "@/components/Settings/ComputeEngineApi/APIKeyCreationDialog.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { pushFeedback } from "@/composables/feedbackQueue";
import { useRootStore } from "@/stores/root";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { UserLimitsE } from "@/js/tierconfig";
import type { UserKey } from "@/types/api/keys";

export type KeyAlertInfo = { keyId: string; name: string; validUntil: number };
export type KeysLoadedPayload = { count: number; expired: KeyAlertInfo[]; expiring: KeyAlertInfo[] };

const EXPIRING_WINDOW_SECONDS = 7 * 24 * 60 * 60;

export default defineComponent({
	name: "ApiKeyTile",
	components: { Add16, ArrowRight16, APIKeyCreationDialog, LimitableActionButton },
	emits: {
		"keys-loaded": (_payload: KeysLoadedPayload) => true,
	},
	setup() {
		return { rootStore: useRootStore(), UserLimitsE };
	},
	data: () => ({
		loading: true,
		creating: false,
		keys: [] as UserKey[],
		showKeyCreationDialog: false,
		keyCreationDialogConfig: null as null | { name: string; key: string },
	}),
	computed: {
		hasKey(): boolean {
			return this.keys.length > 0;
		},
	},
	methods: {
		emitKeysLoaded() {
			const nowSec = Math.floor(Date.now() / 1000);
			const expired: KeyAlertInfo[] = [];
			const expiring: KeyAlertInfo[] = [];
			for (const k of this.keys) {
				if (k.ValidUntil === null || k.ValidUntil === undefined) continue;
				const info = { keyId: k.KeyID, name: k.Name, validUntil: k.ValidUntil };
				if (k.ValidUntil < nowSec) expired.push(info);
				else if (k.ValidUntil - nowSec < EXPIRING_WINDOW_SECONDS) expiring.push(info);
			}
			this.$emit("keys-loaded", { count: this.keys.length, expired, expiring });
		},
		async loadKeys() {
			this.loading = true;
			try {
				const response = await sdk.keys.list();
				const list = response?.Keys ?? [];
				this.keys = Array.isArray(list) ? list : [];
				this.emitKeysLoaded();
			} catch (error) {
				monitoringCaptureError(error, "ApiKeyTile loadKeys");
				this.keys = [];
				this.emitKeysLoaded();
			} finally {
				this.loading = false;
			}
		},
		async createKey() {
			this.creating = true;
			try {
				const defaultName = "Signaloid Cloud Key";
				const defaultDate = new Date();
				defaultDate.setDate(defaultDate.getDate() + 30);
				const validUntil = Math.floor(defaultDate.getTime() / 1000);
				const response = await sdk.keys.create({ Name: defaultName, ValidUntil: validUntil });
				if (!response.Key) {
					throw new Error("API key creation response did not include the key value.");
				}
				this.keyCreationDialogConfig = { key: response.Key, name: response.Name };
				this.showKeyCreationDialog = true;
				this.$posthog?.capture("api_key_created");
				await this.loadKeys();
			} catch (error) {
				// 403 = the tier's API key allowance is used up: expected, not a bug, so keep it out of Sentry
				if (error instanceof SdkError && error.code === ERROR_CODES.API_FORBIDDEN) {
					this.rootStore.tierLimitEventBus.emit({
						type: TierLimitEventTypeE.LimitExceeded,
						affectedLimits: [UserLimitsE.KeyCount],
					});
				} else {
					monitoringCaptureError(error, "ApiKeyTile createKey");
					pushFeedback("We could not create the API key. Try again in a moment.", "error");
				}
			} finally {
				this.creating = false;
			}
		},
	},
	created() {
		this.loadKeys();
	},
});
</script>

<style lang="scss" scoped>
.api-key-tile {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.api-key-tile__title {
	white-space: nowrap;
}

.api-key-tile__settings.v-btn {
	align-self: flex-start;
	padding-left: 0;
}

.api-key-tile__intro {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.api-key-tile__body {
	color: var(--neutral-b-gray-400);
}

.api-key-tile__pictogram {
	flex: 1;
	min-height: var(--sgnl-icon-48);
	background-color: var(--sgnl-brand-icon);
	-webkit-mask-image: url("@/assets/icons/home/key-lifecycle.svg");
	mask-image: url("@/assets/icons/home/key-lifecycle.svg");
	-webkit-mask-size: var(--sgnl-icon-48);
	mask-size: var(--sgnl-icon-48);
	-webkit-mask-repeat: no-repeat;
	mask-repeat: no-repeat;
	-webkit-mask-position: center;
	mask-position: center;
}

.api-key-tile__actions {
	margin-top: auto;
}

.api-key-tile__actions :deep(.v-btn__content) {
	gap: var(--sgnl-space-8);
}
</style>
